import type { HoldBackProductionType } from '@threadheads/shared';
import { toCsvContent } from '@/lib/csv';
import { parseCsvText, parseStrictInt, type CsvParseError } from './csvParse';

/**
 * Stock Hold-Back CSV: serialise the whole grid, and parse an edited copy back
 * into grid cells with a per-row reason for anything refused.
 *
 * PURE. No `t()`, no React, no tRPC. Rejections carry structured reason CODES
 * and the component boundary resolves them to i18n keys, exactly as
 * `csvParse.ts` does with `CsvParseError` — a pure layer that formats English is
 * a permanently untranslatable layer.
 *
 * The file is a ROUND-TRIP DOCUMENT, not a screenshot of the current view: the
 * exporter writes every location and both production types regardless of any
 * on-screen filter, so a re-import can never look complete while silently
 * describing a subset.
 *
 * There is deliberately NO new backend procedure behind this. Accepted cells go
 * through the page's existing `setCell` and then the untouched
 * `previewHoldBackImpact` / `saveHoldBackRules` path.
 */

/**
 * The columns the parser REQUIRES. Order is part of the format.
 *
 * Deliberately four, not five: `stocked` (below) is written on the way out and
 * ignored on the way back in, so a file an operator has trimmed, re-saved from
 * an older export, or hand-built still imports.
 */
export const HOLD_BACK_CSV_HEADERS = [
  'location',
  'production_type',
  'product_type',
  'buffer_units',
] as const;

/**
 * The columns the EXPORTER writes: the four required ones plus `stocked`.
 *
 * `stocked` is a yes/no read-out of whether anything of that type is stocked at
 * that location. It exists because the grid renders an inert cell for that
 * intersection (DESIGN §12.1.2) and a flat CSV otherwise loses that entirely —
 * an operator typing a number into a `no` row gets it rejected on import with
 * no way to have seen it coming. It is INFORMATION, never input: the parser
 * ignores it and re-derives stock from the live grid, so an edited or deleted
 * `stocked` value can never talk the app into accepting a number that binds
 * nothing.
 */
export const HOLD_BACK_CSV_EXPORT_HEADERS = [
  ...HOLD_BACK_CSV_HEADERS,
  'stocked',
] as const;

/** The `stocked` column's two values. ASCII, like POD/PAP: format, not copy. */
const STOCKED_YES = 'yes';
const STOCKED_NO = 'no';

/**
 * Matches the Zod bound on `saveHoldBackRules` (`.int().min(0).max(100000)`).
 * KEEP IN LOCKSTEP with `MAX_HOLD_BACK` in `pages/StockHoldBack.tsx`.
 */
const MAX_HOLD_BACK = 100000;

/**
 * CHANGED-cell ceiling. `saveHoldBackRules` caps upserts at 1000 and deletes at
 * 1000, and `previewHoldBackImpact` caps cells at 1000; a full grid is roughly
 * 200 cells. Capping HERE is what stops a pathological file reaching a boundary
 * that would reject it with a raw 400 the operator cannot act on.
 *
 * It counts cells that DIFFER from what the grid currently holds, not accepted
 * rows. Those are the only cells that reach the save payload — a round trip of
 * an unmodified 1,200-cell export changes nothing and must not be refused for
 * being large.
 */
export const HOLD_BACK_CSV_MAX_CELLS = 1000;

/**
 * Identity of a rule ROW (no location). Owned here so the grid and the parser
 * cannot drift: `StockHoldBack.tsx` imports both this and the separator rather
 * than re-declaring them.
 *
 * The separator is NUL, written as an escape and never as a literal byte (a
 * literal renders as an ordinary space in every diff). Product type is free text
 * and routinely contains spaces; the enum before it cannot contain one, so the
 * split always lands in the right place.
 */
export const HOLD_BACK_KEY_SEP = '\u0000';
export const holdBackRuleKey = (
  productionType: HoldBackProductionType,
  productType: string
): string => `${productionType}${HOLD_BACK_KEY_SEP}${productType}`;

const PRODUCTION_TYPES: readonly HoldBackProductionType[] = ['POD', 'PAP'];

export interface HoldBackCsvLocation {
  id: string;
  name: string;
}

export interface HoldBackCsvRule {
  productionType: HoldBackProductionType;
  productType: string;
}

export interface BuildHoldBackCsvArgs {
  /** Every active rule, in the grid's own display order (POD group first). */
  rules: readonly HoldBackCsvRule[];
  /** Every active location. NOT the filtered/single-location view. */
  locations: readonly HoldBackCsvLocation[];
  /** Stored units for a cell, or null/undefined when no row exists. */
  storedFor: (
    locationId: string,
    productionType: HoldBackProductionType,
    productType: string
  ) => number | null | undefined;
  /**
   * Whether anything of that type is stocked at that location — the grid's own
   * `cellStateFor(...).hasStock`. Drives the `stocked` column, so the file
   * shows which cells are inert instead of leaving the operator to find out on
   * import.
   */
  hasStockFor: (
    locationId: string,
    productionType: HoldBackProductionType,
    productType: string
  ) => boolean;
}

/**
 * One row per (location, rule) cell, including cells with no stored value —
 * whose `buffer_units` field is EMPTY. That blank is what makes "no number set,
 * fall back to the org buffer" expressible on the way back in, as distinct from
 * an explicit `0` ("hold back nothing here").
 *
 * CRLF, no BOM: `downloadCsvFile` owns the BOM (and is idempotent about it), so
 * prefixing here would either double it or fork the decision across two files.
 */
export function buildHoldBackCsv({
  rules,
  locations,
  storedFor,
  hasStockFor,
}: BuildHoldBackCsvArgs): string {
  const body: unknown[][] = [];
  for (const location of locations) {
    for (const rule of rules) {
      const stored = storedFor(location.id, rule.productionType, rule.productType);
      body.push([
        location.name,
        rule.productionType,
        rule.productType,
        stored == null ? '' : stored,
        hasStockFor(location.id, rule.productionType, rule.productType)
          ? STOCKED_YES
          : STOCKED_NO,
      ]);
    }
  }
  return toCsvContent([[...HOLD_BACK_CSV_EXPORT_HEADERS], ...body], { eol: '\r\n' });
}

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

/**
 * ASCII-slugged filename, mirroring `buildStocktakeFilename`'s discipline.
 *
 * No location name appears in it: this file deliberately spans EVERY location,
 * so naming one would misdescribe the contents. The date is slugged anyway (and
 * falls back to a token if it slugs empty) so a caller passing a localised date
 * string cannot produce `stock-hold-back-.csv`.
 */
export function buildHoldBackCsvFilename(today: string): string {
  return `stock-hold-back-${slugify(today) || 'export'}.csv`;
}

/**
 * Filename for the rejected-rows download, date-stamped for the same reason the
 * export is: an operator working through a bad file downloads this more than
 * once, and three files all called `hold-back-failed-rows.csv` in a Downloads
 * folder is a puzzle, not a record.
 */
export function buildHoldBackFailedRowsFilename(today: string): string {
  return `stock-hold-back-failed-rows-${slugify(today) || 'export'}.csv`;
}

export type HoldBackCsvRejectionReason =
  | 'unknown_location'
  | 'ambiguous_location'
  | 'unknown_production_type'
  | 'unknown_product_type'
  | 'invalid_units'
  | 'no_stock';

export interface HoldBackCsvRejection {
  /** 1-based source line, so the operator can find it in their spreadsheet. */
  row: number;
  location: string;
  productionType: string;
  productType: string;
  rawUnits: string;
  reason: HoldBackCsvRejectionReason;
}

export interface HoldBackCsvCell {
  locationId: string;
  productionType: HoldBackProductionType;
  productType: string;
  /** null means "clear the rule" (fall back to the org buffer). */
  bufferUnits: number | null;
}

export type HoldBackCsvFileError =
  | { code: 'parse'; errors: CsvParseError[] }
  /** `count` is CHANGED cells, not parsed rows — see HOLD_BACK_CSV_MAX_CELLS. */
  | { code: 'too_many_cells'; count: number; limit: number };

export interface ParseHoldBackCsvOptions {
  /**
   * Location NAME (lowercased, trimmed) -> location id, built from the
   * locations the caller already holds (see `buildLocationsByName`). A name that
   * is not in this map is a ROW-LEVEL REJECTION, never a passthrough id: a
   * crafted file cannot address a location the session does not already have.
   *
   * `null` marks a name two of those locations share. Names are unique only per
   * organization, so a 3PL client can hold its own location and the 3PL's shared
   * one under one name; guessing which the row meant would stage a number against
   * the wrong place, so the row is rejected instead.
   */
  locationsByName: ReadonlyMap<string, string | null>;
  /** `holdBackRuleKey(productionType, productType)` for every known rule. */
  ruleKeys: ReadonlySet<string>;
  /** The grid's own `cellStateFor(...).editable`. */
  isCellEditable: (
    locationId: string,
    productionType: HoldBackProductionType,
    productType: string
  ) => boolean;
  /**
   * What the GRID currently holds for a cell, pending edits included: `null`
   * for "no number set". Only cells that differ from this count toward
   * `HOLD_BACK_CSV_MAX_CELLS`, because only those reach the save payload.
   *
   * A caller whose cell is mid-edit and unparseable ("5e") should return
   * `NaN`: staging over it IS a change, and `NaN !== anything` says so without
   * needing a third state in this signature.
   */
  currentUnitsFor: (
    locationId: string,
    productionType: HoldBackProductionType,
    productType: string
  ) => number | null;
}

export interface ParseHoldBackCsvResult {
  cells: HoldBackCsvCell[];
  rejected: HoldBackCsvRejection[];
  /** Set when the WHOLE file is unusable; `cells` is then empty. */
  fileError: HoldBackCsvFileError | null;
}

/**
 * Name -> id for `parseHoldBackCsv`, with `null` for a name more than one
 * location carries.
 */
export function buildLocationsByName(
  locations: readonly HoldBackCsvLocation[]
): Map<string, string | null> {
  const byName = new Map<string, string | null>();
  for (const location of locations) {
    const name = location.name.trim().toLowerCase();
    const seen = byName.get(name);
    byName.set(name, seen === undefined || seen === location.id ? location.id : null);
  }
  return byName;
}

const isProductionType = (value: string): value is HoldBackProductionType =>
  (PRODUCTION_TYPES as readonly string[]).includes(value);

/**
 * Parse an edited export back into grid cells.
 *
 * Built on `parseCsvText`, never on a hand-rolled split: that already handles
 * the BOM, quoted fields, embedded newlines and the field-count check, and its
 * `parseStrictInt` yields NaN rather than 0 for junk — the exact bug class that
 * turns a typo into a silent clear.
 *
 * UNKNOWN COLUMNS ARE IGNORED. Only the four required headers are read, so the
 * exporter's `stocked` column, a spreadsheet's stray trailing column and a
 * hand-added notes column all pass through without comment. Anything the parser
 * does not name it does not trust.
 *
 * BLANK IS EXEMPT FROM THE EDITABLE CHECK. A blank cell is "no rule here", which
 * is a no-op on a cell that has no rule; rejecting it would make a clean
 * round trip of an unmodified export report a screenful of `no_stock` rows for
 * every inert intersection the exporter faithfully wrote out. A cell carrying a
 * NUMBER at an intersection that stocks nothing is the real thing to refuse:
 * that is how a grid fills with numbers nobody can account for.
 */
export function parseHoldBackCsv(
  text: string,
  { locationsByName, ruleKeys, isCellEditable, currentUnitsFor }: ParseHoldBackCsvOptions
): ParseHoldBackCsvResult {
  const parsed = parseCsvText(text, {
    requiredColumns: [...HOLD_BACK_CSV_HEADERS],
    numericColumns: ['buffer_units'],
    skipBlankRows: true,
  });

  if (parsed.structuredErrors.length > 0) {
    return { cells: [], rejected: [], fileError: { code: 'parse', errors: parsed.structuredErrors } };
  }

  const cells: HoldBackCsvCell[] = [];
  const rejected: HoldBackCsvRejection[] = [];

  parsed.rawRows.forEach((raw, index) => {
    const row = parsed.sourceRows[index] ?? index + 2;
    const locationText = (raw.location ?? '').trim();
    const productionText = (raw.production_type ?? '').trim();
    const productType = (raw.product_type ?? '').trim();
    const rawUnits = (raw.buffer_units ?? '').trim();

    const reject = (reason: HoldBackCsvRejectionReason) =>
      rejected.push({
        row,
        location: locationText,
        productionType: productionText,
        productType,
        rawUnits,
        reason,
      });

    // Production type and location match case-insensitively after trimming; a
    // spreadsheet round trip routinely re-cases a column.
    const productionType = productionText.toUpperCase();
    if (!isProductionType(productionType)) {
      reject('unknown_production_type');
      return;
    }

    const locationId = locationsByName.get(locationText.toLowerCase());
    if (locationId === undefined) {
      reject('unknown_location');
      return;
    }
    if (locationId === null) {
      reject('ambiguous_location');
      return;
    }

    if (productType === '' || !ruleKeys.has(holdBackRuleKey(productionType, productType))) {
      reject('unknown_product_type');
      return;
    }

    let bufferUnits: number | null = null;
    if (rawUnits !== '') {
      const value = parseStrictInt(rawUnits);
      if (!Number.isInteger(value) || value < 0 || value > MAX_HOLD_BACK) {
        reject('invalid_units');
        return;
      }
      bufferUnits = value;
      if (!isCellEditable(locationId, productionType, productType)) {
        reject('no_stock');
        return;
      }
    }

    cells.push({ locationId, productionType, productType, bufferUnits });
  });

  // Count what would actually CHANGE, not what parsed. A clean round trip of a
  // grid larger than the ceiling changes nothing and must import fine.
  const changedCount = cells.filter(
    (cell) =>
      cell.bufferUnits !==
      currentUnitsFor(cell.locationId, cell.productionType, cell.productType)
  ).length;

  if (changedCount > HOLD_BACK_CSV_MAX_CELLS) {
    return {
      // `rejected` is KEPT. The operator has one shot at this dialog, and a
      // file that is both too large and full of bad rows should say both
      // things — dropping the rejections here means fixing the size only to
      // meet a fresh wall of row errors.
      cells: [],
      rejected,
      fileError: {
        code: 'too_many_cells',
        count: changedCount,
        limit: HOLD_BACK_CSV_MAX_CELLS,
      },
    };
  }

  return { cells, rejected, fileError: null };
}
